import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { io } from "socket.io-client";

const formatPostcodeWithSpace = (raw: string) => {
  if (!raw) return "";
  const s = raw.toUpperCase().replace(/\s+/g, "");
  if (s.length <= 3) return s;
  return s.slice(0, s.length - 3) + " " + s.slice(s.length - 3);
};

const formatUKPhone = (value: string) => {
  let digits = value.replace(/\D/g, "");
  if (!digits.startsWith("44")) digits = "44" + digits;
  let formatted = "+" + digits.slice(0, 2);
  if (digits.length > 2) formatted += " " + digits.slice(2, 6);
  if (digits.length > 6) formatted += " " + digits.slice(6, 11);
  return formatted.trim();
};

const Account: React.FC = () => {
  const [activeTab, setActiveTab] = useState<
    "info" | "edit" | "privacy" | "delete" | "password"
  >("info");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("+44");
  const [postcode, setPostcode] = useState("");
  const [postcodeSuggestions, setPostcodeSuggestions] = useState<string[]>([]);
  const [hasSelectedPostcode, setHasSelectedPostcode] = useState(false);
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [customReason, setCustomReason] = useState("");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [isPasswordVerified, setIsPasswordVerified] = useState(false);
  const [verifiedPassword, setVerifiedPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [profileError, setProfileError] = useState("");
  const [profileSuccess, setProfileSuccess] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deleteSuccess, setDeleteSuccess] = useState("");
  const [passwordMessage, setPasswordMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const API_URL = process.env.REACT_APP_API_URL;
  const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;

  // Loading state pentru încărcarea profilului
  const [profileLoading, setProfileLoading] = useState(true);

  const socket = useRef<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    socket.current = io(SOCKET_URL!, {
      transports: ["websocket"],
      withCredentials: true,
    });

    return () => {
      socket.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!socket.current) return;

    socket.current.on("profileUpdated", (data: any) => {
      console.log("🔥 Profil actualizat de admin:", data);

      // actualizează localStorage + state
      localStorage.setItem("userProfile", JSON.stringify(data.client));

      setName(data.client.name || "");
      setEmail(data.client.email || "");
      setPhone(data.client.phone || "+44");
      setPostcode(data.client.postcode || "");
      setCity(data.client.city || "");
      setAddress(data.client.address || "");
    });

    return () => {
      socket.current?.off("profileUpdated");
    };
  }, []);

  // 🔒 ÎNCARCĂ DATELE DOAR DIN BACKEND (SECURIZAT)
  useEffect(() => {
    const fetchUserProfile = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/auth");
        return;
      }

      try {
        setProfileLoading(true);
        const res = await fetch(`${API_URL}/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!res.ok) {
          if (res.status === 401) {
            localStorage.removeItem("token");
            localStorage.removeItem("userProfile");
            navigate("/auth");
            return;
          }
          throw new Error(`HTTP ${res.status}`);
        }

        const userData = await res.json();
        console.log("✅ Profil utilizator încărcat:", userData);

        // Actualizează state-ul cu datele corecte
        setName(userData.name || "");
        setEmail(userData.email || "");
        setPhone(userData.phone || "+44");
        setPostcode(userData.postcode || "");
        setCity(userData.city || "");
        setAddress(userData.address || "");

        // Actualizează localStorage cu datele corecte
        localStorage.setItem("userProfile", JSON.stringify(userData));

        // Join user room pentru notificări
        if (socket.current && userData.id) {
          socket.current.emit("joinUser", userData.id);
        }
      } catch (error) {
        console.error("❌ Eroare încărcare profil:", error);
        setProfileError(
          "Eroare la încărcarea profilului. Vă rugăm să vă reconectați.",
        );
      } finally {
        setProfileLoading(false);
      }
    };

    fetchUserProfile();
  }, [navigate]);

  // Autocomplete Postcode (păstrează logica existentă)
  useEffect(() => {
    if (!postcode || postcode.replace(/\s+/g, "").length < 2) {
      setPostcodeSuggestions([]);
      return;
    }

    if (hasSelectedPostcode) return;

    const timeout = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://api.postcodes.io/postcodes/${encodeURIComponent(postcode)}/autocomplete`,
        );
        const data = await res.json();
        if (data?.result) {
          setPostcodeSuggestions(
            data.result.map((pc: string) => formatPostcodeWithSpace(pc)),
          );
        } else {
          setPostcodeSuggestions([]);
        }
      } catch {
        setPostcodeSuggestions([]);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [postcode, hasSelectedPostcode]);

  const handlePostcodeSelect = async (selected: string) => {
    setHasSelectedPostcode(true);
    setPostcode(selected);
    setPostcodeSuggestions([]);
    try {
      const res = await fetch(`https://api.postcodes.io/postcodes/${selected}`);
      const data = await res.json();
      if (data.status === 200) {
        let detectedCity =
          data.result.admin_district ||
          data.result.post_town ||
          data.result.parish ||
          "";
        if (
          data.result.region === "London" ||
          data.result.nuts === "UKI" ||
          (data.result.primary_care_trust &&
            data.result.primary_care_trust.includes("London"))
        ) {
          detectedCity = "London";
        }
        setCity(detectedCity);
        setAddress("");
      }
    } catch (error) {
      console.error("Error fetching postcode details:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setProfileError("");
    setProfileSuccess("");

    if (!privacyAccepted) {
      setProfileError(
        "Trebuie să accepti politica de confidențialitate înainte de a salva.",
      );
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      setProfileError(
        "Nu sunteți autentificat. Vă rugăm să vă conectați din nou.",
      );
      return;
    }

    try {
      console.log("🔤 Trimit spre server:", {
        name,
        email,
        phone,
        postcode,
        city,
        address,
      });

      const res = await fetch(`${API_URL}/me`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: name || "",
          email: email || "",
          phone: phone || "",
          postcode: postcode || "",
          city: city || "",
          address: address || "",
        }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error("❌ Eroare la update:", res.status, errorText);
        setProfileError(
          `A apărut o eroare la salvare (cod ${res.status}). ${errorText || "Încercă din nou."}`,
        );
        return;
      }

      const updatedUserData = await res.json();
      console.log("✅ Răspuns server:", updatedUserData);

      // actualizăm localStorage + state
      localStorage.setItem("userProfile", JSON.stringify(updatedUserData));

      setName(updatedUserData.name || "");
      setEmail(updatedUserData.email || "");
      setPhone(updatedUserData.phone || "+44");
      setPostcode(updatedUserData.postcode || "");
      setCity(updatedUserData.city || "");
      setAddress(updatedUserData.address || "");

      setProfileSuccess("Datele au fost salvate cu succes!");
    } catch (error) {
      console.error("🔥 Eroare generală la update:", error);
      setProfileError(
        "A apărut o eroare de rețea. Te rugăm să încerci din nou.",
      );
    }
  };

  const handleDeleteRequest = async () => {
    setDeleteError("");
    setDeleteSuccess("");

    if (!deleteReason) {
      setDeleteError("Please select a reason for account deletion.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/request-delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          reason: deleteReason,
          customReason: deleteReason === "other" ? customReason : null,
        }),
      });

      if (!res.ok) throw new Error("Failed to submit request");
      setDeleteSuccess(
        "We have received your deletion request. It will be processed within 48h.",
      );
    } catch (err) {
      console.error("Error submitting delete request:", err);
      setDeleteError("Something went wrong. Please try again.");
    }
  };

  // Loading state
  if (profileLoading) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto mt-8 mb-6 bg-white dark:bg-neutral-700 rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-lg">Loading profile...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto mt-8 mb-6 bg-white dark:bg-neutral-600 rounded-2xl shadow-lg p-6 grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="flex flex-col space-y-3 border-r dark:border-neutral-700 pr-4">
          <button
            className={`text-left px-3 py-2 rounded-lg transition ${
              activeTab === "info"
                ? "bg-blue-500 text-white"
                : "hover:bg-gray-100 dark:hover:bg-neutral-700"
            }`}
            onClick={() => setActiveTab("info")}
          >
            Information
          </button>
          <button
            className={`text-left px-3 py-2 rounded-lg transition ${
              activeTab === "edit"
                ? "bg-blue-500 text-white"
                : "hover:bg-gray-100 dark:hover:bg-neutral-700"
            }`}
            onClick={() => setActiveTab("edit")}
          >
            Edit
          </button>
          <button
            className={`text-left px-3 py-2 rounded-lg transition ${
              activeTab === "privacy"
                ? "bg-blue-500 text-white"
                : "hover:bg-gray-100 dark:hover:bg-neutral-700"
            }`}
            onClick={() => setShowPrivacy(true)}
          >
            Privacy Policy
          </button>
          <button
            className={`text-left px-3 py-2 rounded-lg transition ${
              activeTab === "password"
                ? "bg-blue-500 text-white"
                : "hover:bg-gray-100 dark:hover:bg-neutral-700"
            }`}
            onClick={() => setActiveTab("password")}
          >
            Change Password
          </button>
          <button
            className={`text-left px-3 py-2 rounded-lg transition ${
              activeTab === "delete"
                ? "bg-red-500 text-white"
                : "hover:bg-red-100 dark:hover:bg-red-700"
            }`}
            onClick={() => setActiveTab("delete")}
          >
            Delete Account
          </button>

          <button
            onClick={() => navigate("/my-orders")}
            className="mt-6 text-center bg-gray-700 hover:bg-gray-600 text-white py-2 rounded-lg"
          >
            ← Back to My Orders
          </button>
        </div>

        {/* Main Content */}
        <div className="md:col-span-3">
          {activeTab === "info" && (
            <div>
              <h2 className="text-xl font-bold mb-4">Account Information</h2>
              <p>
                <strong>Name:</strong> {name || "Not set"}
              </p>
              <p>
                <strong>Email:</strong> {email || "Not set"}
              </p>
              <p>
                <strong>Phone:</strong> {phone || "Not set"}
              </p>
              <p>
                <strong>Postcode:</strong> {postcode || "Not set"}
              </p>
              <p>
                <strong>City:</strong> {city || "Not set"}
              </p>
              <p>
                <strong>Address:</strong> {address || "Not set"}
              </p>
            </div>
          )}

          {activeTab === "edit" && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {profileError && (
                <div className="bg-red-100 text-red-700 p-3 rounded-lg text-sm">
                  {profileError}
                </div>
              )}
              {profileSuccess && (
                <div className="bg-green-100 text-green-700 p-3 rounded-lg text-sm">
                  {profileSuccess}
                </div>
              )}
              {/* Name */}
              <div>
                <label className="block text-sm font-medium mb-2">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full border rounded-lg p-3 bg-gray-50 dark:bg-neutral-500"
                />
              </div>
              {/* Email */}
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border rounded-lg p-3 bg-gray-50 dark:bg-neutral-500"
                />
              </div>
              {/* Phone */}
              <div>
                <label className="block text-sm font-medium mb-2">Phone</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(formatUKPhone(e.target.value))}
                  className="w-full border rounded-lg p-3 bg-gray-50 dark:bg-neutral-500 tracking-wider"
                  placeholder="+44 1234 567890"
                />
              </div>
              {/* Postcode */}
              <div className="relative">
                <label className="block text-sm font-medium mb-2">
                  Postcode
                </label>
                <input
                  type="text"
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value)}
                  className="w-full border rounded-lg p-3 bg-gray-50 dark:bg-neutral-500"
                  placeholder="e.g. IG7 5QA"
                />
                {postcodeSuggestions.length > 0 && (
                  <ul className="absolute z-10 bg-white border rounded-lg shadow-md w-full mt-1 max-h-40 overflow-y-auto">
                    {postcodeSuggestions.map((s, i) => (
                      <li
                        key={i}
                        onClick={() => handlePostcodeSelect(s)}
                        className="px-4 py-2 text-gray-800 dark:text-neutral-100 hover:bg-gray-100 dark:hover:bg-neutral-600 cursor-pointer"
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {/* City */}
              <div>
                <label className="block text-sm font-medium mb-2">City</label>
                <input
                  type="text"
                  value={city}
                  readOnly
                  className="w-full border rounded-lg p-3 bg-gray-100 dark:bg-neutral-500"
                  placeholder="Auto-filled from postcode"
                />
              </div>
              {/* Address */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Full Address
                </label>
                <textarea
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="w-full border rounded-lg p-3 bg-gray-50 dark:bg-neutral-500"
                  placeholder="Street, number, apartment..."
                />
              </div>
              {/* Privacy checkbox */}
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                />
                <span className="text-sm">
                  I accept the{" "}
                  <button
                    type="button"
                    className="text-blue-600 underline"
                    onClick={() => setShowPrivacy(true)}
                  >
                    Privacy Policy
                  </button>
                </span>
              </div>
              <button
                type="submit"
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-semibold"
              >
                Save Changes
              </button>
            </form>
          )}

          {activeTab === "password" && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold mb-4">Change Password</h2>

              {!isPasswordVerified ? (
                // === STEP 1: Verificare parolă curentă ===
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    setIsUpdatingPassword(true);
                    try {
                      const token = localStorage.getItem("token");
                      const res = await fetch(`${API_URL}/verify-password`, {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                          Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({ currentPassword }),
                      });

                      if (!res.ok)
                        throw new Error("Incorrect current password");

                      setVerifiedPassword(currentPassword);
                      setIsPasswordVerified(true);
                      setCurrentPassword("");
                    } catch (err) {
                      setPasswordError(
                        "Current password is incorrect. Please try again.",
                      );
                    } finally {
                      setIsUpdatingPassword(false);
                    }
                  }}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Current Password
                    </label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className={`w-full border rounded-lg p-3 bg-gray-50 dark:bg-neutral-500 ${
                        passwordError ? "border-red-500" : ""
                      }`}
                      required
                    />
                    {passwordError && (
                      <p className="text-red-500 text-sm mt-1">
                        {passwordError}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isUpdatingPassword}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold disabled:opacity-50"
                  >
                    {isUpdatingPassword ? "Verifying..." : "Verify Password"}
                  </button>
                </form>
              ) : (
                // === STEP 2: Introducere parolă nouă ===
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();

                    if (newPassword.length < 6) {
                      setPasswordMessage({
                        type: "error",
                        text: "Password must be at least 6 characters long.",
                      });
                      return;
                    }
                    if (newPassword !== confirmPassword) {
                      setPasswordMessage({
                        type: "error",
                        text: "Passwords do not match!",
                      });
                      return;
                    }

                    setIsUpdatingPassword(true);
                    try {
                      const token = localStorage.getItem("token");
                      const res = await fetch(`${API_URL}/change-password`, {
                        method: "PUT",
                        headers: {
                          "Content-Type": "application/json",
                          Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                          oldPassword: verifiedPassword,
                          newPassword,
                        }),
                      });

                      if (!res.ok) throw new Error("Failed to update password");

                      setPasswordMessage({
                        type: "success",
                        text: "Password updated successfully!",
                      });
                      setNewPassword("");
                      setConfirmPassword("");
                      setIsPasswordVerified(false);
                      setActiveTab("info");
                    } catch (err) {
                      setPasswordMessage({
                        type: "error",
                        text: "Failed to update password. Please try again.",
                      });
                    } finally {
                      setIsUpdatingPassword(false);
                    }
                  }}
                  className="space-y-4"
                >
                  {/* 🟢 Mesaj de succes sau eroare */}
                  {passwordMessage && (
                    <p
                      className={`text-sm p-2 rounded-lg ${
                        passwordMessage.type === "success"
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {passwordMessage.text}
                    </p>
                  )}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      New Password
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full border rounded-lg p-3 bg-gray-50 dark:bg-neutral-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full border rounded-lg p-3 bg-gray-50 dark:bg-neutral-500"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isUpdatingPassword}
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-semibold disabled:opacity-50"
                  >
                    {isUpdatingPassword ? "Saving..." : "Save New Password"}
                  </button>
                </form>
              )}
            </div>
          )}

          {activeTab === "delete" && (
            <div>
              {deleteError && (
                <div className="bg-red-100 text-red-700 p-3 rounded-lg text-sm mb-3">
                  {deleteError}
                </div>
              )}
              {deleteSuccess && (
                <div className="bg-green-100 text-green-700 p-3 rounded-lg text-sm mb-3">
                  {deleteSuccess}
                </div>
              )}
              <h2 className="text-xl font-bold text-red-600 mb-4">
                Delete Account
              </h2>
              <p className="mb-4 text-gray-500">
                Deleting your account is <strong>permanent</strong> and cannot
                be undone. Once approved by our team, all your orders and
                history will be removed.
              </p>

              {/* Motivul ștergerii */}
              <label className="block text-sm font-medium mb-2">
                Reason for deletion
              </label>
              <select
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                className="border rounded-lg p-3 w-full mb-3 text-black dark:text-black bg-white dark:bg-white"
              >
                <option value="" className="text-black">
                  Select reason
                </option>
                <option value="no-longer-needed" className="text-black">
                  I no longer need the service
                </option>
                <option value="privacy-concerns" className="text-black">
                  Privacy concerns
                </option>
                <option value="too-expensive" className="text-black">
                  Too expensive
                </option>
                <option value="other" className="text-black">
                  Other
                </option>
              </select>

              {/* Dacă alege "Other", apare inputul */}
              {deleteReason === "other" && (
                <textarea
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  placeholder="Please specify your reason..."
                  className="border rounded-lg p-3 w-full mb-3"
                />
              )}

              <button
                onClick={handleDeleteRequest}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
              >
                Request Account Deletion
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Privacy Policy Modal */}
      {showPrivacy && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-neutral-500 rounded-2xl shadow-xl p-6 max-w-3xl w-full relative">
            <button
              onClick={() => setShowPrivacy(false)}
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-900"
            >
              ✕
            </button>
            <h2 className="text-xl font-bold mb-4">Privacy Policy</h2>
            <div className="max-h-[60vh] overflow-y-auto text-sm space-y-4">
              <p>
                <strong>Last updated:</strong> September 15, 2025
              </p>
              <p>
                This Privacy Policy explains how we collect, use, disclose, and
                protect your personal information when you use our services,
                including registering for an account, placing an order, or
                contacting us.
              </p>
              <p>
                <strong>
                  By creating an account or submitting your data on this site,
                  you consent to the collection, processing, and storage of your
                  personal data in accordance with this Privacy Policy.
                </strong>
              </p>
              <h3 className="font-semibold">1. Information We Collect</h3>
              <p>
                We may collect personal information such as your name, email
                address, phone number, postal address, and any other details you
                voluntarily provide when you register, fill in forms, or
                communicate with us.
              </p>
              <h3 className="font-semibold">2. How We Use Your Data</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li>To create and manage your account</li>
                <li>To process and deliver orders</li>
                <li>To respond to your questions or support requests</li>
                <li>To send administrative notifications</li>
                <li>To comply with legal obligations</li>
              </ul>
              <h3 className="font-semibold">3. Sharing of Data</h3>
              <p>
                We may share your data with trusted service providers (such as
                couriers, payment processors, or IT support) only to the extent
                necessary to provide our services. We do not sell your data to
                third parties.
              </p>
              <h3 className="font-semibold">4. Data Retention</h3>
              <p>
                We retain your personal information only for as long as
                necessary to fulfill the purposes outlined in this Privacy
                Policy, or as required by law.
              </p>
              <h3 className="font-semibold">5. Your Rights</h3>
              <p>
                You have the right to access, correct, or request deletion of
                your personal data. You may also withdraw your consent at any
                time by contacting us.
              </p>
              <h3 className="font-semibold">6. Contact</h3>
              <p>
                If you have any questions or requests regarding this Privacy
                Policy, please contact us at:{" "}
                <a
                  href="mailto:admin@felixdent.co.uk"
                  className="text-blue-600 underline"
                >
                  admin@felixdent.co.uk
                </a>
              </p>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Account;
